import React from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Footer.css';
import { trackEvent } from '../lib/analytics';
import { useBusinessHours, DAY_ORDER, DAY_LABELS_SHORT, formatDayHours } from '../lib/businessHours';

const EVENTBRITE_URL = 'https://www.eventbrite.com/o/royal-bar-grill-120928056108';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const businessHours = useBusinessHours();

  const scrollToSection = (sectionId) => {
    // If not on homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const goToReservations = () => {
    trackEvent('reserve_seat_click', { link_location: 'footer' });
    window.open(EVENTBRITE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/6ywx5xvf_CFB2E630-E067-4D50-B2DB-A530D292F3D8.png" 
              alt="Royal Bar & Grill" 
              className="footer-logo"
            />
            <p className="footer-tagline">Premium American classics and authentic Indian fusion in Albany, Oregon.</p>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">Quick Links</h4>
            <nav className="footer-nav">
              <button onClick={() => scrollToSection('menu')} className="footer-link">Menu</button>
              <button onClick={() => scrollToSection('gallery')} className="footer-link">Gallery</button>
              <button onClick={() => { navigate('/meet-the-team'); window.scrollTo(0, 0); }} className="footer-link" data-testid="meet-the-team-footer">Meet the Team</button>
              <button onClick={() => scrollToSection('events')} className="footer-link">Events</button>
              <button onClick={goToReservations} className="footer-link" data-testid="reserve-seat-footer">Reserve a Seat</button>
              <button onClick={() => scrollToSection('location')} className="footer-link">Location</button>
              <button onClick={() => scrollToSection('contact')} className="footer-link">Contact</button>
              <button onClick={() => { navigate('/quick-access'); window.scrollTo(0, 0); }} className="footer-link" data-testid="quick-access-footer">Quick Access</button>
            </nav>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">Contact</h4>
            <div className="footer-contact">
              <div className="footer-contact-item">
                <MapPin size={18} />
                <span>859 Belmont Ave SW #120<br />Albany, OR 97321</span>
              </div>
              <div className="footer-contact-item">
                <Phone size={18} />
                <a href="tel:+14582534058" onClick={() => trackEvent('phone_click', { link_location: 'footer' })}>(458) 253-4058</a>
              </div>
              <div className="footer-contact-item">
                <Mail size={18} />
                <a href="mailto:novumventuresholdings@gmail.com">novumventuresholdings@gmail.com</a>
              </div>
            </div>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">Hours</h4>
            <div className="footer-hours">
              {businessHours && DAY_ORDER.map((key) => (
                <div key={`footer-hours-${key}`} className="footer-hours-row" data-testid={`footer-hours-${key}`}>
                  <span>{DAY_LABELS_SHORT[key]}</span>
                  <span data-testid={`footer-hours-time-${key}`}>{formatDayHours(businessHours[key])}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Royal Bar & Grill. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;